import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import Loader from '../../Components/Utility/Loader/Loader';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';

import * as action from './Store/SystemMasters.action';
import typeConstants from './Store/SystemMasters.type';

function CustomAttrSave(isLoading) {
  return isLoading ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />;
}

const tempRow = (tuple) => ({
  ...tuple,
  _id: '',
  isNew: true,
  isActive: true,
  name: '',
  isEmpty: false,
});

function ProductTypeProcessMaster() {
  const dispatch = useDispatch();
  const {
    SystemMastersDetails,
    UserDetails,
    Users: { logInRights },
  } = useSelector((s) => s);

  const { t } = useTranslation('common');
  const { userInfo } = UserDetails || {};
  const loggedInUserId = userInfo?.userId;

  const { pcTypeList, productTypeProcessData, category1List, category2List, category3List, category4List } = SystemMastersDetails || {};

  const { CheckActionPermission } = useComponent();
  const [userRights, setUserRights] = useState({});
  useEffect(() => {
    if (logInRights) setUserRights(logInRights);
  }, [logInRights]);

  const canAddEdit = CheckActionPermission(userRights, 'masters', 'productTypeProcesses', 'addEdit');
  const canDelete = CheckActionPermission(userRights, 'masters', 'productTypeProcesses', 'delete');

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [confirmToggle, setConfirmToggle] = useState({ open: false, id: null, isActive: true });
  const [deleting, setDeleting] = useState(false);
  const [nameErrors, setNameErrors] = useState({});

  const recomputeNameErrors = (list) => {
    const buckets = {};
    const errs = {};
    list.forEach((r, idx) => {
      const nm = (r?.name || '').trim().toLowerCase();
      const nameLength = (r?.name || '').trim().length;

      if (nameLength > 30) {
        errs[idx] = t('PRODUCT_TYPE_PROCESS.MAX_30_CHAR_ALLOWED');
      }

      if (!nm) return;
      if (!buckets[nm]) buckets[nm] = [];
      buckets[nm].push(idx);
    });
    Object.values(buckets).forEach((indexes) => {
      if (indexes.length > 1) {
        indexes.forEach((i) => {
          if (!errs[i]) {
            errs[i] = t('PRODUCT_TYPE_PROCESS.DUPLICATE_NAME') || 'Duplicate name';
          }
        });
      }
    });
    setNameErrors(errs);
    return errs;
  };

  const handleNameChange = (index, value) => {
    const trimmedValue = value.replace(/^\s+/, '');
    setRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        name: trimmedValue,
        isEmpty: !trimmedValue || !trimmedValue.trim(),
      };
      recomputeNameErrors(next);
      return next;
    });
  };

  const [productType, setProductType] = useState(null);
  const [cat1, setCat1] = useState(null);
  const [cat2, setCat2] = useState(null);
  const [cat3, setCat3] = useState(null);
  const [cat4, setCat4] = useState(null);

  const handleToggleAccordion = async () => {
    const toOpen = !open;
    setOpen(toOpen);
    if (toOpen) {
      setLoading(true);
      await dispatch(action.getPCTypesDetails({ isActive: true }, true));
      await Promise.all([
        dispatch(action.getCategory1Details({ categoryType: 1, isActive: true, loggedInUserId })),
        dispatch(action.getCategory2Details({ categoryType: 2, isActive: true, loggedInUserId })),
      ]);
      setLoading(false);
    } else {
      setProductType(null);
      setCat1(null);
      setCat2(null);
      setCat3(null);
      setCat4(null);
      setRows([]);
      setOriginalRows([]);
      setNameErrors({});
      setSaving(false);
      setConfirmDelete({ open: false, id: null });
      setConfirmToggle({ open: false, id: null, isActive: true });
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (cat1?._id && cat2?._id) {
      setCat3(null);
      setCat4(null);
      dispatch(action.getCategory3Details({ categoryType: 3, isActive: true, loggedInUserId, categoryOneId: cat1?._id, categoryTwoId: cat2?._id }));
    } else if (!cat1?._id || !cat2?._id) {
      setCat3(null);
      setCat4(null);
      dispatch({
        type: typeConstants.GET_ALL_CATEGORY_3,
        payload: { data: [], fromCategory4: false },
      });
      dispatch({
        type: typeConstants.GET_ALL_CATEGORY_4,
        payload: [],
      });
    }
  }, [cat1, cat2, dispatch, loggedInUserId]);

  useEffect(() => {
    if (cat1?._id && cat2?._id && cat3?._id) {
      dispatch(
        action.getCategory4Details({
          categoryType: 4,
          isActive: true,
          loggedInUserId,
          categoryOneId: cat1?._id,
          categoryTwoId: cat2?._id,
          categoryThreeId: cat3?._id,
        }),
      );
    } else if (!cat3?._id) {
      setCat4(null);
      dispatch({
        type: typeConstants.GET_ALL_CATEGORY_4,
        payload: [],
      });
    }
  }, [cat1, cat2, cat3, dispatch, loggedInUserId]);

  const tuple = useMemo(
    () => ({
      productTypeId: productType?._id,
      categoryOneId: cat1?._id || null,
      categoryTwoId: cat2?._id || null,
      categoryThreeId: cat3?._id || null,
      categoryFourId: cat4?._id || null,
    }),
    [productType, cat1, cat2, cat3, cat4],
  );

  useEffect(() => {
    const fetchList = async () => {
      if (!productType?._id) return;
      const params = {
        productTypeId: productType?._id,
        categoryOneId: cat1?._id,
        categoryTwoId: cat2?._id,
        categoryThreeId: cat3?._id,
        categoryFourId: cat4?._id,
      };
      await dispatch(action.getProductTypeProcessDetails(params));
    };
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, cat1, cat2, cat3, cat4]);

  useEffect(() => {
    const source = Array.isArray(productTypeProcessData) ? productTypeProcessData : [];
    const rowsWithValidation = source.map((r) => ({
      ...r,
      isEmpty: false,
    }));
    setRows(rowsWithValidation);
    setOriginalRows(source.map((r) => ({ ...r })));
    recomputeNameErrors(rowsWithValidation);
  }, [productTypeProcessData]);

  const addRow = () => {
    if (!productType?._id) {
      toasterMsg(t('PRODUCT_TYPE_PROCESS.SELECT_PRODUCT_TYPE_FIRST'), 'danger');
      return;
    }
    setRows((prev) => [...prev, tempRow(tuple)]);
  };

  const saveRows = async () => {
    setSaving(true);
    try {
      if (!isEmpty(rows)) {
        const emptyFields = rows.filter((r) => r.isNew && (r.name === null || r.name === ''));

        if (isEmpty(emptyFields)) {
          const spacesOnlyFields = rows.filter((r) => r.isNew && !isEmpty(r?.name) && isEmpty(r?.name?.trim()));
          if (!isEmpty(spacesOnlyFields)) {
            setSaving(false);
            return;
          }

          const currentErrors = recomputeNameErrors(rows);
          if (Object.keys(currentErrors).length > 0) {
            setSaving(false);
            return;
          }

          const dataToSave = rows
            .filter((r) => {
              if (r.isNew) return true;
              if (!r._id) return false;
              const original = originalRows.find((or) => or._id === r._id);
              if (!original) return false;
              return (r.name?.trim() || '') !== (original.name?.trim() || '');
            })
            .map((r) => ({
              ...r,
              name: r.name?.trim(),
            }));

          if (dataToSave.length > 0) {
            await dispatch(
              action.saveProductTypeProcessToSystemMaster(
                {
                  loggedInUserId,
                  processes: JSON.stringify(dataToSave),
                },
                t,
              ),
            );
            await dispatch(action.clearProductTypeProcessMasterData());
            await dispatch(action.getProductTypeProcessDetails(tuple));
          }
        } else {
          const updatedRows = rows.map((r) => (r.isNew && (r.name === null || r.name === '') ? { ...r, isEmpty: true } : r));
          setRows(updatedRows);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (id) => setConfirmDelete({ open: true, id });
  const askToggle = (data) => setConfirmToggle({ open: true, id: data?._id, isActive: !data?.isActive });

  const hasErrors = Object.keys(nameErrors || {}).length > 0;
  const hasNewRows = !isEmpty(rows.filter((r) => r.isNew));

  const hasModifiedRows = useMemo(() => {
    if (isEmpty(rows) || isEmpty(originalRows)) {
      return hasNewRows;
    }
    const originalMap = new Map(originalRows.map((r) => [r._id, r]));
    return rows.some((row) => {
      if (row.isNew) return true;
      if (!row._id) return false;
      const original = originalMap.get(row._id);
      if (!original) return false;
      return (row.name?.trim() || '') !== (original.name?.trim() || '');
    });
  }, [rows, originalRows, hasNewRows]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await dispatch(
        action.deleteProductTypeProcessFromSystemMasters(
          {
            productTypeProcessId: confirmDelete.id,
            loggedInUserId,
          },
          t,
        ),
      );
      setConfirmDelete({ open: false, id: null });
      await dispatch(action.clearProductTypeProcessMasterData());
      await dispatch(action.getProductTypeProcessDetails(tuple));
    } finally {
      setDeleting(false);
    }
  };

  const doToggle = async () => {
    setDeleting(true);
    try {
      await dispatch(action.setProductTypeProcessActiveInactive({ id: confirmToggle.id, loggedInUserId, isActive: confirmToggle.isActive }, t));
      setConfirmToggle({ open: false, id: null });
      await dispatch(action.clearProductTypeProcessMasterData());
      await dispatch(action.getProductTypeProcessDetails(tuple));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Accordion className="mb-4 master-accordian  custom-attribute-master">
        <Card className="card">
          <Card.Header>
            <div className="title-container">
              <p>{t('PRODUCT_TYPE_PROCESS.TITLE')}</p>
            </div>
            <div className="action-title-btns">
              <CustomTooltip tooltipText={open ? t('CUSTOM_ATTRIBUTES.CLOSE') : t('CUSTOM_ATTRIBUTES.OPEN')}>
                <button type="button" onClick={handleToggleAccordion} className="btn d-inline-flex p-0">
                  <i className={`icon-new-down-arrow-icon f-24${open ? ' icon-inverse' : ''}`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {open && (
            <Card.Body>
              {loading ? (
                <Loader />
              ) : (
                <>
                  <div className="select-container">
                    <div className="common-react-select mb-0 pr-2" style={{ minWidth: 220 }}>
                      <Select
                        options={pcTypeList || []}
                        placeholder={t('PRODUCT_TYPE_PROCESS.SELECT_PRODUCT_TYPE')}
                        getOptionLabel={(o) => `${o?.name || ''}`}
                        getOptionValue={(o) => `${o?._id}`}
                        value={productType}
                        maxMenuHeight={130}
                        className="common-select"
                        classNamePrefix="common-select"
                        onChange={setProductType}
                      />
                    </div>
                    <div className="common-react-select mb-0 pr-2" style={{ minWidth: 220 }}>
                      <Select
                        options={category1List || []}
                        placeholder={t('PRODUCT_TYPE_PROCESS.SELECT_CATEGORY1')}
                        getOptionLabel={(o) => `${o?.name || ''}`}
                        getOptionValue={(o) => `${o?._id}`}
                        value={cat1}
                        maxMenuHeight={130}
                        className="common-select"
                        classNamePrefix="common-select"
                        onChange={setCat1}
                        isClearable
                      />
                    </div>
                    <div className="common-react-select mb-0 pr-2" style={{ minWidth: 220 }}>
                      <Select
                        options={category2List || []}
                        placeholder={t('PRODUCT_TYPE_PROCESS.SELECT_CATEGORY2')}
                        getOptionLabel={(o) => `${o?.name || ''}`}
                        getOptionValue={(o) => `${o?._id}`}
                        value={cat2}
                        maxMenuHeight={130}
                        className="common-select"
                        classNamePrefix="common-select"
                        onChange={setCat2}
                        isClearable
                      />
                    </div>
                    <div className="common-react-select mb-0 pr-2" style={{ minWidth: 220 }}>
                      <Select
                        options={category3List || []}
                        placeholder={t('PRODUCT_TYPE_PROCESS.SELECT_CATEGORY3')}
                        getOptionLabel={(o) => `${o?.name || ''}`}
                        getOptionValue={(o) => `${o?._id}`}
                        value={cat3}
                        maxMenuHeight={130}
                        className="common-select"
                        classNamePrefix="common-select"
                        onChange={setCat3}
                        isClearable
                      />
                    </div>
                    <div className="common-react-select mb-0 pr-2" style={{ minWidth: 220 }}>
                      <Select
                        options={category4List || []}
                        placeholder={t('PRODUCT_TYPE_PROCESS.SELECT_CATEGORY4')}
                        getOptionLabel={(o) => `${o?.name || ''}`}
                        getOptionValue={(o) => `${o?._id}`}
                        value={cat4}
                        maxMenuHeight={130}
                        className="common-select"
                        classNamePrefix="common-select"
                        onChange={setCat4}
                        isClearable
                      />
                    </div>
                  </div>
                  <div className="common-scrollbar flex-fill">
                    <ul>
                      {(rows || []).map((r, i) => (
                        <li
                          key={
                            r?._id ||
                            `${r.productTypeId}-${r.categoryOneId || 'x'}-${r.categoryTwoId || 'x'}-${r.categoryThreeId || 'x'}-${r.categoryFourId || 'x'}`
                          }
                        >
                          <div className="input-container">
                            <input
                              type="text"
                              placeholder={t('PRODUCT_TYPE_PROCESS.ENTER_CUSTOM_NAME')}
                              value={r?.name || ''}
                              maxLength={30}
                              onChange={(e) => handleNameChange(i, e.target.value)}
                            />
                            {nameErrors[i] && <label className="text-danger p-1">{nameErrors[i]}</label>}

                            {r?.isNew && !isEmpty(r?.name) && isEmpty(r?.name?.trim()) && (
                              <label className="text-danger p-1">
                                {t('PRODUCT_TYPE_PROCESS.TITLE')} {t('SYSTEM_MASTER_TABLE.ENTRY_IS_INVALID')}
                              </label>
                            )}
                            {r?.isNew && r?.isEmpty && (r?.name === null || r?.name === '') && !(!isEmpty(r?.name) && isEmpty(r?.name?.trim())) && (
                              <label className="text-danger p-1">
                                {t('PRODUCT_TYPE_PROCESS.TITLE')} {t('SYSTEM_MASTER_TABLE.ENTRY_REQUIRED')}
                              </label>
                            )}
                          </div>
                          <div className="input-action d-flex">
                            {r?._id && canAddEdit && (
                              <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.ACTIVE/INACTIVE')}>
                                <button
                                  type="button"
                                  onClick={() => askToggle(r)}
                                  className="btn p-0 custom-action mr-0"
                                  aria-label={r?.isActive ? 'Active' : 'Inactive'}
                                >
                                  <span className={r.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                                </button>
                              </CustomTooltip>
                            )}
                            {canDelete && (
                              <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.DELETE')}>
                                <button
                                  type="button"
                                  onClick={() => (r?._id ? askDelete(r?._id) : setRows((prev) => prev.filter((_, idx) => idx !== i)))}
                                  className="btn custom-action mr-0"
                                  aria-label="delete"
                                >
                                  <span className="icon-Delete f-24" />
                                </button>
                              </CustomTooltip>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="license-btn-container">
                    {canAddEdit && (
                      <>
                        <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_TOOLTIP')}>
                          <button
                            type="button"
                            onClick={addRow}
                            className="btn btn-outline sm ml-auto mr-0 mt-16 p-0"
                            disabled={!productType?._id}
                            aria-label="Add"
                          >
                            <span className="icon-Add text-grey" />
                          </button>
                        </CustomTooltip>
                        <div className="section-custom-save">
                          <button aria-label="Save" type="button" className="btn p-0" onClick={saveRows} disabled={!hasModifiedRows || hasErrors}>
                            {CustomAttrSave(saving)}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>

      {confirmDelete.open && (
        <ConfirmationPopup
          onOpen={confirmDelete.open}
          onConfirm={doDelete}
          title={t('BOM_TYPE_MASTER.DELETE_FROM_SYSTEM_MASTER')}
          message={t('BOM_TYPE_MASTER.ARE_YOU_SURE_DELETE_ITEM')}
          toggle={() => setConfirmDelete({ open: false, id: null })}
          loading={deleting}
        />
      )}

      {confirmToggle.open && (
        <ConfirmationPopup
          onOpen={confirmToggle.open}
          onConfirm={doToggle}
          title={t('BOM_TYPE_MASTER.ACTIVATE_DEACTIVATE')}
          message={t('BOM_TYPE_MASTER.ARE_YOU_SURE_ACTIVATE_DEACTIVATE')}
          toggle={() => setConfirmToggle({ open: false, id: null })}
          loading={deleting}
        />
      )}
    </>
  );
}

export default ProductTypeProcessMaster;
